import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useIndicateurTdbStore } from './useIndicateurTdbStore'
import { useScrollIntoView } from '@/features/interface/composables'
import { storeToRefs } from 'pinia'

export function useIndicateurTdbScroll() {
  const containerRef = ref<HTMLElement | null>(null)
  const currentIndex = ref(0)
  const showDots = ref(false)
  const store = useIndicateurTdbStore()
  const { authorizedDocumentCards } = storeToRefs(store)

  const { makeId, makeVisible } = useScrollIntoView()

  const AUTO_SCROLL_INTERVAL = 5000
  const CARDS_PER_STEP = 2
  let autoScrollTimer: ReturnType<typeof setInterval> | null = null
  const isUserInteracting = ref(false)

  function getCardCount() {
    return Object.keys(authorizedDocumentCards.value).length
  }

  function startAutoScroll() {
    stopAutoScroll()

    if (!showDots.value || getCardCount() <= CARDS_PER_STEP) return

    autoScrollTimer = setInterval(() => {
      if (isUserInteracting.value) return

      const totalCards = getCardCount()
      const nextIndex = currentIndex.value + CARDS_PER_STEP

      if (nextIndex >= totalCards) {
        scrollToIndex(0)
      } else {
        scrollToIndex(nextIndex)
      }
    }, AUTO_SCROLL_INTERVAL)
  }

  function stopAutoScroll() {
    if (autoScrollTimer) {
      clearInterval(autoScrollTimer)
      autoScrollTimer = null
    }
  }

  // Pause temporaire lors d'interaction utilisateur
  function pauseAutoScroll() {
    isUserInteracting.value = true
    // Reprendre après 10 secondes d'inactivité
    setTimeout(() => {
      isUserInteracting.value = false
    }, 10000)
  }

  function checkScrollPossible() {
    nextTick(() => {
      const el = containerRef.value
      if (!el) return
      showDots.value = el.scrollWidth > el.clientWidth

      // Démarrer l'auto-scroll si le défilement est possible
      if (showDots.value) {
        startAutoScroll()
      } else {
        stopAutoScroll()
      }
    })
  }

  function onScroll() {
    const el = containerRef.value
    if (!el) return

    const containerRect = el.getBoundingClientRect()
    const containerCenter = containerRect.left + containerRect.width / 2

    let closestIndex = 0
    let minDistance = Infinity

    Object.keys(authorizedDocumentCards.value).forEach((_, index) => {
      const cardEl = document.getElementById(makeId(index))
      if (!cardEl) return

      const cardRect = cardEl.getBoundingClientRect()
      const cardCenter = cardRect.left + cardRect.width / 2
      const distance = Math.abs(cardCenter - containerCenter)

      if (distance < minDistance) {
        minDistance = distance
        closestIndex = index
      }
    })

    currentIndex.value = closestIndex
  }

  function scrollToIndex(i: number) {
    makeVisible(i)
    currentIndex.value = i
  }

  // Clic manuel sur un dot = pause de l'auto-scroll
  function handleDotClick(i: number) {
    pauseAutoScroll()
    scrollToIndex(i)
  }

  onMounted(() => {
    window.addEventListener('resize', checkScrollPossible)
    checkScrollPossible()
  })

  onUnmounted(() => {
    window.removeEventListener('resize', checkScrollPossible)
    stopAutoScroll()
  })

  watch(authorizedDocumentCards, checkScrollPossible, { deep: true })

  return {
    containerRef,
    currentIndex,
    showDots,
    onScroll,
    scrollToIndex: handleDotClick,
    checkScrollPossible,
    makeId,
    pauseAutoScroll,
  }
}
